import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher, useLoaderData } from 'react-router';
import { useLogImpression } from '@archipro-website/tracker';

import { RoccoLink } from '@rocco/components/link';
import { useRailTileClickReport } from '~/ui-rocco/hooks/tile-click-event-for-section';
import { LOAD_MORE_FEATURED_PROJECTS_ACTION } from '~/routes/actions.load-more-featured-projects';
import { FEATURED_PROJECTS_PAGE_SIZE } from '~/modules/product/util/featured-projects-config';
import type { loader } from '~/routes/_app.product.$urlSegment';
import {
    DetailTileMedia,
    detailTileActionsFor,
} from '~/ui-rocco/pages/detail-shared';
import { LoadMoreButton } from '~/ui-rocco/pages/detail-shared/LoadMoreButton';
import { TileMobileActionsSlot } from '~/modules/moodboards/components';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import { useProductAccordionRows } from './spec-accordion/use-product-accordion-rows';
import { getFeaturedInsideParts } from './featured-inside-statement';

/** Projects shown initially and revealed per "Load More" tap. */
const REVEAL_STEP = 4;

/** A mapped project tile (SSR page 0 and the action return this shape). */
type Tile = ReturnType<
    typeof useLoaderData<typeof loader>
>['featuredProjects']['FeaturedProjects']['edges'][number]['node'];

/**
 * One project the product is specified in. Reports what the legacy page's
 * "Projects using this product" tiles did on the phone: the impression, and
 * on a tap the rail's own event with the site-wide tile click.
 */
const SpecifiedInTile = ({ tile }: { tile: Tile }) => {
    const tileRef = useRef<HTMLDivElement>(null);
    const reportClick = useRailTileClickReport(
        'ProductFeaturedProjects',
        'ProjectTile',
    );
    useLogImpression({
        data: {
            type: 'LibraryItem_Directory',
            itemID: tile.id,
            professionalID: tile.professional?.id ?? 0,
        },
        ref: tileRef,
    });

    const actions = detailTileActionsFor({
        id: tile.id,
        link: tile.link,
        title: tile.title,
        images: tile.images,
        professional: tile.professional,
        professionalLink: tile.professional?.link,
        website: tile.website,
        pinType: 'ProjectPin',
    });

    return (
        <div ref={tileRef} className="relative">
            <RoccoLink
                to={tile.link}
                className="block w-full"
                onClick={() =>
                    reportClick({
                        id: tile.id,
                        title: tile.title,
                        professionalId: tile.professional?.id,
                    })
                }
            >
                <DetailTileMedia
                    src={tile.images[0]?.src}
                    alt={tile.images[0]?.alt ?? tile.title}
                    className="bg-neutral-200 aspect-[592/430]"
                    imgClassName="max-w-none"
                    pinId={tile.id}
                    pinType="ProjectPin"
                    enquiry={actions.enquiry}
                    visitWebsite={actions.visitWebsite}
                    share={actions.share}
                    tileInfo={actions.tileInfo}
                />
                <p className="m-0 truncate font-sans leading-[1.3] font-medium text-[#161616] max-md:pt-2 max-md:text-sm md:pt-[0.625rem] md:text-[1.375rem]">
                    {tile.title}
                </p>
                {tile.professional?.name ? (
                    <p className="m-0 truncate font-sans leading-[1.3] font-normal text-[#161616] opacity-40 max-md:text-sm md:text-[1.375rem]">
                        {tile.professional.name}
                    </p>
                ) : null}
            </RoccoLink>
            {/* Mobile actions live outside the tile link so a tap on the
                `•••` can never navigate. */}
            <TileMobileActionsSlot
                pinId={tile.id}
                pinType="ProjectPin"
                enquiry={actions.enquiry}
                visitWebsite={actions.visitWebsite}
                share={actions.share}
                tileInfo={actions.tileInfo}
            />
        </div>
    );
};

/**
 * Specified in — the standalone **mobile** counterpart of the desktop
 * Tagged-Projects statement (F11). The desktop click-to-expand reads as a
 * plain statement on touch (nothing signals it opens), so mobile gets the
 * Similar-Products treatment instead: hairline + "(NN — Specified in)"
 * eyebrow, the two-tone "Featured inside {project}" heading, and a 2-col
 * project grid with no reveal step — four tiles up front, the shared Load
 * More pill for the rest. The loader SSRs the first page of projects;
 * further pages append via the load-more-featured-projects action once the
 * local pool runs dry. Self-hides when no tagged project has a banner image.
 */
export const SpecifiedInSection = () => {
    const {
        product,
        featuredProjects: { FeaturedProjects },
    } = useLoaderData<typeof loader>();
    const { rows } = useProductAccordionRows();

    const [visibleCount, setVisibleCount] = useState(REVEAL_STEP);

    // Pagination: page 0 is SSR'd; further pages are fetched + appended once
    // the reveal window approaches the end of the local pool.
    const fetcher = useFetcher<{
        projects: Tile[];
        hasMore: boolean;
        totalCount: number;
    }>();
    const [appended, setAppended] = useState<Tile[]>([]);
    const [serverHasMore, setServerHasMore] = useState<boolean | null>(null);
    const nextOffsetRef = useRef(FEATURED_PROJECTS_PAGE_SIZE);
    const requestedOffsetRef = useRef(-1);
    const processedOffsetRef = useRef(-1);

    // Navigating to another product reuses this component instance (same
    // route), so React state survives. Reset the reveal window and pagination
    // during render — an effect would land a paint late, flashing the previous
    // product's load-more results mixed into the new grid.
    const [renderedLink, setRenderedLink] = useState(product.Link);
    if (renderedLink !== product.Link) {
        setRenderedLink(product.Link);
        setVisibleCount(REVEAL_STEP);
        setAppended([]);
        setServerHasMore(null);
        nextOffsetRef.current = FEATURED_PROJECTS_PAGE_SIZE;
        requestedOffsetRef.current = -1;
        processedOffsetRef.current = -1;
    }

    const initialTiles = useMemo(
        () => FeaturedProjects.edges.map((edge) => edge.node),
        [FeaturedProjects],
    );

    const fetchPage = useCallback(
        (offset: number) => {
            if (requestedOffsetRef.current === offset) return;
            requestedOffsetRef.current = offset;
            const params = new URLSearchParams({
                link: product.Link,
                offset: String(offset),
                limit: String(FEATURED_PROJECTS_PAGE_SIZE),
            });
            fetcher.load(
                `${LOAD_MORE_FEATURED_PROJECTS_ACTION}?${params.toString()}`,
            );
        },
        [fetcher, product.Link],
    );

    // Append each fetched page once, skipping anything already loaded. An
    // empty payload means the action rejected the request or hit a 500 —
    // clear the in-flight guard so the same offset can be retried.
    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data) return;
        if (processedOffsetRef.current === requestedOffsetRef.current) return;

        const incoming = fetcher.data.projects ?? [];
        if (incoming.length === 0) {
            requestedOffsetRef.current = -1;
            return;
        }

        processedOffsetRef.current = requestedOffsetRef.current;
        setAppended((prev) => {
            const seen = new Set(
                [...initialTiles, ...prev].map((tile) => tile.id),
            );
            const fresh = incoming.filter((tile) => !seen.has(tile.id));
            return fresh.length ? [...prev, ...fresh] : prev;
        });
        setServerHasMore(Boolean(fetcher.data.hasMore));
        nextOffsetRef.current =
            requestedOffsetRef.current + FEATURED_PROJECTS_PAGE_SIZE;
    }, [fetcher.state, fetcher.data, initialTiles]);

    const tiles = useMemo(
        () =>
            [...initialTiles, ...appended].filter(
                (tile) => tile.images?.[0]?.src,
            ),
        [initialTiles, appended],
    );

    const hasMoreOnServer =
        serverHasMore ?? FeaturedProjects.pageInfo.hasNextPage;

    const onLoadMore = () => {
        const nextVisible = visibleCount + REVEAL_STEP;
        // Top up the local pool before the reveal window runs it dry, so the
        // tap after this one reveals instantly instead of waiting on fetch.
        if (
            tiles.length < nextVisible + REVEAL_STEP &&
            hasMoreOnServer &&
            fetcher.state === 'idle'
        ) {
            fetchPage(nextOffsetRef.current);
        }
        setVisibleCount(nextVisible);
    };

    const firstTile = tiles[0];
    if (!firstTile) return null;

    // The connection's totalCount is the real total even though only a page
    // is loaded, so the "+ N others" count stays accurate.
    const { projectLabel, othersSuffix } = getFeaturedInsideParts(
        firstTile.title,
        FeaturedProjects.pageInfo.totalCount - 1,
    );

    const shown = tiles.slice(0, visibleCount);
    const showLoadMore = tiles.length > visibleCount || hasMoreOnServer;
    // Only surface the loading state when the tap is actually waiting on the
    // server (pool exhausted) — background top-ups stay silent.
    const isLoadingMore =
        fetcher.state !== 'idle' && tiles.length <= visibleCount;

    const number = String(rows.length + 1).padStart(2, '0');

    return (
        <section className="bg-ground text-[#1f1f21] max-md:px-6 md:px-[3rem]">
            <SectionSpacer
                height={120}
                mobileHeight={48}
                unit="rem"
                className="bg-ground"
            />
            <div className="h-px w-full bg-black/20" />
            <SectionLabel className="mt-[1.062rem] block max-md:mt-3 max-md:text-[0.875rem]">
                ({number} — Specified in)
            </SectionLabel>
            <div className="max-md:mt-6 md:mt-[3rem]">
                <h2 className="m-0 font-sans font-normal text-[#1f1f21] max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em] md:text-[12.5rem] md:leading-[0.9] md:tracking-[-0.042em]">
                    Featured inside{' '}
                    <span className="text-[#767676] md:text-[#8f8f8f]">
                        {projectLabel}
                    </span>
                    {othersSuffix}
                </h2>

                <div className="grid grid-cols-2 max-md:mt-6 max-md:gap-x-3.5 max-md:gap-y-[3.6875rem] md:mt-[2.125rem] md:gap-x-3 md:gap-y-[3rem]">
                    {shown.map((tile) => (
                        <SpecifiedInTile key={tile.id} tile={tile} />
                    ))}
                </div>

                {showLoadMore ? (
                    <LoadMoreButton
                        className="mt-[3rem] max-md:mt-10"
                        isLoading={isLoadingMore}
                        onClick={onLoadMore}
                    />
                ) : null}
            </div>
        </section>
    );
};
